// İnceleme ve onay: beklenen (başlangıç + sayım sırasındaki hareketler), sayılan, fark ve tutar;
// seçilen satırları yeniden saydırma; onayda fazla/eksik stoğa işlenir. Tamamlanan sayımda sonuç gösterilir.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Paper, Radio, RadioGroup, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { exportReviewExcel } from './stockCountExcel';
import { printCountReport } from './stockCountPrint';
import { fmtMoney, fmtQty, stockCountService, type ReviewLine, type StockCountDetail, type StockCountReview } from './stockCountService';

type Filter = 'all' | 'diff' | 'over' | 'uncounted' | 'moved';

interface Props {
  count: StockCountDetail;
  /** Onay yetkisi (yönetici için alt yetki; sunucu yine denetler). */
  canApprove: boolean;
  onChanged: () => void;
}

const ReviewPanel: React.FC<Props> = ({ count, canApprove, onChanged }) => {
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);
  const completed = count.status === 'COMPLETED';

  const [review, setReview] = useState<StockCountReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>(completed ? 'diff' : 'diff');
  const [selected, setSelected] = useState<string[]>([]);
  const [approveOpen, setApproveOpen] = useState(false);
  const [zeroUncounted, setZeroUncounted] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    if (!review) return;
    setExporting(true);
    try { await exportReviewExcel(count, review); } catch (e: any) { setError(e?.message || 'Excel oluşturulamadı.'); } finally { setExporting(false); }
  };
  const printReport = () => {
    if (!review) return;
    try { printCountReport(count, review); } catch (e: any) { setError(e?.message || 'Tutanak açılamadı.'); }
  };

  const load = useCallback(async (zero: boolean) => {
    setLoading(true);
    setError(null);
    try {
      setReview(await stockCountService.review(count.id, zero));
    } catch (e: any) {
      setError(e?.message || tt('İnceleme yüklenemedi.', 'Review could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [count.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(false); }, [load]);

  const lines = review?.lines ?? [];
  const counts = useMemo(() => ({
    all: lines.length,
    diff: lines.filter((l) => (l.differenceQty ?? 0) !== 0).length,
    over: lines.filter((l) => l.overTolerance).length,
    uncounted: lines.filter((l) => l.countedQty == null).length,
    moved: lines.filter((l) => l.movedDuringCount).length,
  }), [lines]);
  const shown = useMemo(() => lines.filter((l) => {
    if (filter === 'diff') return (l.differenceQty ?? 0) !== 0;
    if (filter === 'over') return l.overTolerance;
    if (filter === 'uncounted') return l.countedQty == null;
    if (filter === 'moved') return l.movedDuringCount;
    return true;
  }), [lines, filter]);

  const recount = async (ids: string[]) => {
    setBusy(true);
    try {
      await stockCountService.recount(count.id, ids);
      onChanged();
    } catch (e: any) {
      setError(e?.message || tt('Yeniden saydırılamadı.', 'Could not send for recount.'));
    } finally {
      setBusy(false);
    }
  };

  const openApprove = async () => {
    setZeroUncounted(false);
    setNote('');
    setApproveOpen(true);
  };

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      await stockCountService.approve(count.id, zeroUncounted, note);
      setApproveOpen(false);
      onChanged();
    } catch (e: any) {
      setError(e?.message || tt('Onaylanamadı.', 'Could not approve.'));
      setApproveOpen(false);
    } finally {
      setBusy(false);
    }
  };

  // Onay penceresi için sıfırlama seçeneğinin etkisi
  const [zeroPreview, setZeroPreview] = useState<StockCountReview | null>(null);
  useEffect(() => {
    if (!approveOpen || count.type !== 'FULL') { setZeroPreview(null); return; }
    stockCountService.review(count.id, zeroUncounted).then(setZeroPreview).catch(() => setZeroPreview(null));
  }, [approveOpen, zeroUncounted, count.id, count.type]);
  const preview = zeroPreview ?? review;

  const chip = (key: Filter, label: string) => (
    <Chip key={key} size="small" label={`${label} ${counts[key]}`} color={filter === key ? 'primary' : 'default'}
      variant={filter === key ? 'filled' : 'outlined'} onClick={() => setFilter(key)} />
  );

  const diffCell = (l: ReviewLine) => {
    if (l.differenceQty == null) return <Typography variant="body2" color="text.secondary">—</Typography>;
    const color = l.differenceQty > 0 ? 'success.main' : l.differenceQty < 0 ? 'error.main' : 'text.secondary';
    return <Typography variant="body2" fontWeight={700} color={color}>{l.differenceQty > 0 ? '+' : ''}{fmtQty(l.differenceQty)}</Typography>;
  };

  if (loading && !review) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {review && (
        <>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={exporting}>
              {tt('Excel indir', 'Download Excel')}
            </Button>
            <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={printReport}>
              {completed ? tt('Sayım tutanağı (PDF)', 'Count report (PDF)') : tt('Ön tutanak yazdır', 'Print draft report')}
            </Button>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              { label: tt('Sayılan satır', 'Counted rows'), value: `${review.countedLines} / ${review.lines.length}`, color: undefined },
              { label: tt('Farklı satır', 'Rows with difference'), value: String(review.differentLines), color: undefined },
              { label: tt('Fazla', 'Surplus'), value: `+${fmtMoney(review.surplusValue)}`, color: 'success.main' },
              { label: tt('Eksik', 'Shortage'), value: fmtMoney(review.shortageValue), color: 'error.main' },
            ].map((k) => (
              <Card key={k.label} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                  <Typography variant="h6" fontWeight={700} color={k.color}>{k.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {!completed && review.movedDuringCountLines > 0 && (
            <Alert severity="warning">
              {tt(`${review.movedDuringCountLines} üründe sayım sırasında hareket oldu (satış, alış, transfer); beklenen miktar bu hareketlerle düzeltildi.`,
                `${review.movedDuringCountLines} products moved during the count; expected quantity was adjusted for them.`)}
            </Alert>
          )}
          {completed && (
            <Alert severity="success">
              {tt(`Onaylandı. ${count.assignees.length ? '' : ''}Farklar "Sayım Fazlası" ve "Sayım Eksiği" stok hareketleriyle işlendi; stok hareketleri raporunda ${count.number} referansıyla görünür.`,
                `Approved. Differences were posted as count surplus/shortage movements with reference ${count.number}.`)}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {chip('all', tt('Tümü', 'All'))}
            {chip('diff', tt('Farklı', 'Different'))}
            {counts.over > 0 && chip('over', tt('Tolerans üstü', 'Over tolerance'))}
            {chip('uncounted', tt('Sayılmayan', 'Uncounted'))}
            {counts.moved > 0 && chip('moved', tt('Sayımda hareket', 'Moved during count'))}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 } }}>
                  {!completed && <TableCell padding="checkbox" />}
                  <TableCell>{tt('Ürün', 'Product')}</TableCell>
                  <TableCell align="right">{tt('Başlangıçta', 'At start')}</TableCell>
                  <TableCell align="right">{tt('Sayım sırasında', 'During count')}</TableCell>
                  <TableCell align="right">{tt('Beklenen', 'Expected')}</TableCell>
                  <TableCell align="right">{tt('Sayılan', 'Counted')}</TableCell>
                  <TableCell align="right">{tt('Fark', 'Diff.')}</TableCell>
                  <TableCell align="right">{tt('Birim maliyet', 'Unit cost')}</TableCell>
                  <TableCell align="right">{tt('Tutar', 'Amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((l) => (
                  <TableRow key={l.id} sx={l.overTolerance ? { bgcolor: 'warning.light' } : undefined}>
                    {!completed && (
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.includes(l.id)}
                          onChange={(e) => setSelected((prev) => (e.target.checked ? [...prev, l.id] : prev.filter((x) => x !== l.id)))} />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{l.itemName}{l.lotNo ? ` · ${l.lotNo}` : ''}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {l.overTolerance && <Chip size="small" color="warning" label={tt('Tolerans üstü', 'Over tolerance')} />}
                        {l.isOutOfScope && <Chip size="small" color="warning" variant="outlined" label={tt('Kapsam dışı', 'Out of scope')} />}
                        {l.hasManualEntry && <Chip size="small" variant="outlined" label={tt('Elle girildi', 'Typed')} />}
                        {l.recountRequested && <Chip size="small" color="info" label={tt('Yeniden sayılıyor', 'Recount')} />}
                        {l.countedBy.length > 0 && <Typography variant="caption" color="text.secondary">{l.countedBy.join(', ')}</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmtQty(l.snapshotQty)}</TableCell>
                    <TableCell align="right">{l.movementQty ? `⟳ ${l.movementQty > 0 ? '+' : ''}${fmtQty(l.movementQty)}` : '0'}</TableCell>
                    <TableCell align="right">{fmtQty(l.expectedQty)} {l.unit}</TableCell>
                    <TableCell align="right">{l.countedQty == null ? <Typography variant="body2" color="text.secondary">{tt('sayılmadı', 'not counted')}</Typography> : fmtQty(l.countedQty)}</TableCell>
                    <TableCell align="right">{diffCell(l)}</TableCell>
                    <TableCell align="right">{fmtMoney(l.unitCost)}</TableCell>
                    <TableCell align="right" sx={{ color: (l.differenceValue ?? 0) < 0 ? 'error.main' : (l.differenceValue ?? 0) > 0 ? 'success.main' : undefined, fontWeight: 600 }}>
                      {l.differenceValue == null ? '—' : fmtMoney(l.differenceValue)}
                    </TableCell>
                  </TableRow>
                ))}
                {shown.length === 0 && (
                  <TableRow><TableCell colSpan={completed ? 8 : 9} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    {tt('Bu filtrede satır yok.', 'No rows in this filter.')}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {review.unknownBarcodes.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>{tt('Tanınmayan barkodlar', 'Unknown barcodes')}</Typography>
              {review.unknownBarcodes.map((u) => (
                <Typography key={u.barcode} variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {u.barcode} · {fmtQty(u.quantity)} · {u.countedBy.join(', ')}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary">
                {tt('Bu barkodlar stoğa işlenmez. Ürünle eşleştirmek için barkodu ilgili stok kartına ekleyip satırı yeniden saydırın.',
                  'These barcodes are not posted. Add the barcode to the product card and recount to include them.')}
              </Typography>
            </Paper>
          )}

          {!completed && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button variant="outlined" disabled={busy || !canApprove || selected.length === 0} onClick={() => recount(selected)}>
                {tt(`Seçilenleri yeniden saydır (${selected.length})`, `Recount selected (${selected.length})`)}
              </Button>
              <Button variant="outlined" disabled={busy || !canApprove || count.status !== 'REVIEW'} onClick={() => recount([])}>
                {tt('Sayıma geri gönder', 'Send back to counting')}
              </Button>
              <Button variant="contained" color="success" disabled={busy || !canApprove} onClick={openApprove}>
                {tt('Onayla ve stoğa işle', 'Approve and post')}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={approveOpen} onClose={busy ? undefined : () => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{tt('Sayımı onayla', 'Approve count')}</DialogTitle>
        <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
          {preview && (
            <Alert severity="info">
              {tt(`${preview.differentLines} ürünün stoğu değişecek: fazla ${fmtMoney(preview.surplusValue)}, eksik ${fmtMoney(preview.shortageValue)}.`,
                `${preview.differentLines} products will change: surplus ${fmtMoney(preview.surplusValue)}, shortage ${fmtMoney(preview.shortageValue)}.`)}
            </Alert>
          )}
          {count.type === 'FULL' && (review?.uncountedLines ?? 0) > 0 && (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {tt(`Sayılmayan ${review?.uncountedLines} ürün:`, `${review?.uncountedLines} uncounted products:`)}
              </Typography>
              <RadioGroup value={zeroUncounted ? 'zero' : 'keep'} onChange={(e) => setZeroUncounted(e.target.value === 'zero')}>
                <FormControlLabel value="keep" control={<Radio size="small" />} label={tt('Olduğu gibi bırak', 'Leave as is')} />
                <FormControlLabel value="zero" control={<Radio size="small" />} label={tt('Sıfırla (rafta yok sayılır)', 'Set to zero (not on shelf)')} />
              </RadioGroup>
            </Box>
          )}
          {count.type !== 'FULL' && (review?.uncountedLines ?? 0) > 0 && (
            <Typography variant="body2" color="text.secondary">
              {tt(`Sayılmayan ${review?.uncountedLines} ürünün stoğuna dokunulmaz.`, `${review?.uncountedLines} uncounted products are left unchanged.`)}
            </Typography>
          )}
          <TextField size="small" label={tt('Onay notu', 'Approval note')} value={note} onChange={(e) => setNote(e.target.value)} />
          <Typography variant="caption" color="text.secondary">
            {tt('Onaydan sonra sayım kilitlenir; düzeltme gerekirse yeni sayım yapılır.', 'The count is locked after approval; use a new count for corrections.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={busy}>{tt('Vazgeç', 'Cancel')}</Button>
          <Button variant="contained" color="success" onClick={approve} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : tt('Onayla ve stoğa işle', 'Approve and post')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewPanel;
